/**
 * Campaign List Page
 *
 * Lists autonomous campaigns with status and progress.
 * Helps users identify which campaigns were launched and track their work.
 *
 * Route: /campaigns/autonomous
 */

import { ArrowRight, Calendar, Target, Users, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

function CampaignList() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archived, setArchived] = useState(false);

  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const clientId = userProfile?.client_id;

  const loadCampaigns = async ({ append = false, nextOffset = 0 } = {}) => {
    if (!clientId) {
      setError('Client profile not loaded (missing client_id).');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const params = new URLSearchParams();
      params.set('client_id', clientId);
      params.set('limit', String(pageSize));
      params.set('offset', String(nextOffset));
      params.set('archived', archived ? 'true' : 'false');
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (q && q.trim()) params.set('q', q.trim());

      const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/list?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch campaigns');
      const data = await res.json();

      const nextCampaigns = data.campaigns || [];
      setCampaigns((prev) => (append ? [...prev, ...nextCampaigns] : nextCampaigns));
      setHasMore(Boolean(data.has_more));
      setOffset(nextOffset);
    } catch (e) {
      console.error('Error loading campaigns:', e);
      setError(e?.message || 'Failed to load campaigns');
      setCampaigns(append ? campaigns : []);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset pagination whenever filters change
    setLoading(true);
    setOffset(0);
    setHasMore(false);
    setCampaigns([]);
    loadCampaigns({ append: false, nextOffset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archived, statusFilter, q, clientId]);

  const getStatusBadge = (c) => {
    if (c.paused) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Paused</span>;
    if (c.status === 'completed') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Completed</span>;
    if (c.status === 'executing') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Executing</span>;
    if (c.status === 'failed') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Failed</span>;
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{c.status || 'Unknown'}</span>;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Autonomous Campaigns</h1>
            <p className="text-gray-600 mt-1">Track campaigns and view their progress</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/campaigns/autonomous/new')}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Job title or goal..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              />
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="all">All</option>
                <option value="executing">Executing</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex items-end gap-2">
              <input
                id="archivedToggle"
                type="checkbox"
                checked={archived}
                onChange={(e) => setArchived(e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="archivedToggle" className="text-sm text-gray-700">
                Show archived
              </label>
            </div>
          </div>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h2>
            <p className="text-gray-500 mb-6">Launch your first autonomous campaign to get started.</p>
            <button
              onClick={() => navigate('/campaigns/autonomous/new')}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            >
              New Campaign
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((c) => (
              <div
                key={c.goal_id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-orange-200 transition cursor-pointer"
                onClick={() => {
                  if (c.session_id) {
                    localStorage.setItem(`goal_id_${c.session_id}`, c.goal_id);
                    navigate(`/campaigns/autonomous/${c.session_id}/results`);
                  } else {
                    // Legacy: use goal_id as session placeholder; results page will resolve via goal API
                    localStorage.setItem(`goal_id_${c.goal_id}`, c.goal_id);
                    navigate(`/campaigns/autonomous/${c.goal_id}/results`);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 truncate">{c.job_title || 'Untitled Campaign'}</h3>
                      {getStatusBadge(c)}
                      {c.archived ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          Archived
                        </span>
                      ) : null}
                    </div>
                    {c.natural_language_goal && (
                      <p className="text-sm text-gray-600 truncate mb-2">{c.natural_language_goal}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        Sourced: {c.sourced_count} | Qualified: {c.qualified_count} | Contacted: {c.contacted_count} | Scheduled: {c.scheduled_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(c.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const res = await fetch(
                            `${API_BASE_URL}/api/campaigns/autonomous/${c.goal_id}/archive?client_id=${encodeURIComponent(clientId)}`,
                            {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ archived: !c.archived }),
                            }
                          );
                          if (!res.ok) throw new Error('Failed to update archive state');
                          const data = await res.json();
                          setCampaigns((prev) =>
                            prev.map((x) =>
                              x.goal_id === c.goal_id ? { ...x, archived: data.archived } : x
                            )
                          );
                        } catch (err) {
                          console.error(err);
                          alert(err?.message || 'Failed to archive campaign');
                        }
                      }}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 bg-white"
                      aria-label={c.archived ? 'Unarchive campaign' : 'Archive campaign'}
                    >
                      {c.archived ? 'Unarchive' : 'Archive'}
                    </button>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && campaigns.length > 0 && hasMore ? (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => loadCampaigns({ append: true, nextOffset: offset + pageSize })}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            >
              Load more
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CampaignList;
